import { useCallback, useEffect, useState } from 'react';
import { centerPerfAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';

/* 센터 실적 «기준 데이터 → BPO 가져오기» (운영·시스템 관리자 · 기준 데이터 관리 조직만 — 서버가 다시 판정한다).
 * · 파일 → «BPO 미리보기»(서버가 파싱만 · DB 쓰기 0) → 항목(고객사리스트·센터계획 연도별 · 출고 RAW)을 체크 → «선택 항목 적용».
 * · 적용은 미리보기한 그 파일일 때만 켠다(파일을 바꾸면 미리보기·체크가 지워진다). 서버는 같은 파일을 다시 파싱해
 *   체크한 항목만 한 트랜잭션으로 통째로 교체한다 — 고객사·월 목표 = 그 연도만, 출고 RAW = 전체.
 * · 오류가 난 항목은 체크할 수 없다. 확인창에 지워질 행 수를 보인다.
 * · 요일지수(연도) 시트(선택 — 없어도 «파일에 없는 항목»에 넣지 않는다) = 날짜별 가중치. 통째 교체가 아니라
 *   그 블록에 있는 날짜만 넣거나 바꾼다(다른 날짜·수기 값은 그대로).
 * · 센터실적 시트(선택) = 대시보드 상수(최대실적·표준 CAPA·보관 CAPA·현보관량). 교체가 아니라 숫자가 있는 칸만 덮어쓴다
 *   (빈 칸은 기존 값을 지우지 않는다). 미리보기 표(bpo-constants)가 센터·소계·총계별 «지금 → 파일»을 보이고 바뀌는 칸을 강조한다.
 */
export const KIND_LABELS = {
  customers: '고객사 마스터', targets: '월 목표', legacy: '과거 출고 RAW', weights: '날짜별 가중치', constants: '센터실적 상수',
};
const MISSING_LABELS = { customers: '고객사리스트(연도)', targets: '센터계획(연도)', legacy: '출고 RAW' };
const TARGET_KIND_LABELS = { center: '센터', subtotal: '소계', total: '총계' };
// 교체하지 않는 항목(지울 행이 없다) — 0행이어도 «지워집니다» 경고를 내지 않는다.
const MERGE_KINDS = ['weights', 'constants'];

const card = { background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' };
const th = { padding: '7px 9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '5px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top' };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
const inp = { padding: '5px 8px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' };
const btn = (bg, color = '#fff') => ({ padding: '6px 12px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 });
const hint = { fontSize: '12px', color: COLORS.textMuted, marginTop: '8px' };

const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};
const fmtNum = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
};
export const itemLabel = (it) => `${KIND_LABELS[it.kind] || it.kind}${it.year ? ` ${it.year}` : ''}`;

// 센터실적 상수 — 숫자가 있는 칸 수·바뀌는 칸 수(미리보기 constants 가 없으면 파서 요약의 칸 수).
const constantCounts = (it) => ({
  filled: it.constants?.filled_cells ?? it.summary?.filled_cells ?? 0,
  changed: it.constants?.changed_cells ?? 0,
});

// 현보관량 기준일 — 파일 기준일 | 없으면 적용한 날(KST).
export function constantsAsOfText(it) {
  const asOf = it.summary?.as_of;
  return asOf ? `파일 기준일 ${asOf}` : '파일 기준일 없음 — 적용한 날(KST)';
}

// 행이 0 인데 지금 값이 있으면 «지워진다» 로 경고한다(교체 = 통째로 지우고 넣는다).
// 날짜별 가중치는 그 블록 날짜만 바꾸므로 0행이어도 지워지지 않는다. 센터실적 상수는 숫자가 있는 칸만 덮어쓴다.
export function replaceText(it) {
  const existing = it.existing_rows || 0;
  if (it.kind === 'weights') return `그 블록 날짜만 넣거나 바꿈(그 해 지금 ${fmtNum(existing)}행 · 다른 날짜 그대로)`;
  if (it.kind === 'constants') {
    const { filled, changed } = constantCounts(it);
    return `숫자가 있는 ${fmtNum(filled)}칸을 덮어씁니다(바뀌는 칸 ${fmtNum(changed)}개 · 빈 칸은 그대로)`;
  }
  if (!it.rows && existing > 0) {
    return it.kind === 'legacy' ? `전체 ${fmtNum(existing)}행이 지워집니다` : `그 연도 ${fmtNum(existing)}행이 지워집니다`;
  }
  return `지금 ${fmtNum(existing)}행 → 교체`;
}

function periodText(it) {
  const s = it.summary || {};
  if (it.kind === 'legacy') {
    if (!s.date_from) return '—';
    const years = Object.entries(s.by_year || {}).map(([y, n]) => `${y}년 ${fmtNum(n)}행`).join(' · ');
    return `${s.date_from} ~ ${s.date_to}${years ? ` (${years})` : ''}`;
  }
  if (it.kind === 'targets' && (s.months || []).length) {
    return `${it.year}년 ${s.months[0]}~${s.months[s.months.length - 1]}월 · 센터 ${s.centers || 0}개`;
  }
  if (it.kind === 'weights') {
    if (!s.date_from) return '—';
    return `${s.date_from} ~ ${s.date_to} · ${(s.months || []).join(', ')}`;
  }
  if (it.kind === 'constants') return `현보관량 기준일: ${constantsAsOfText(it)}`;
  return it.year ? `${it.year}년` : '—';
}

// 이력 «항목별 행 수» 한 칸.
export function historyItemText(x) {
  const [kind, year] = String(x.key || '').split(':');
  if (kind === 'constants') return `${KIND_LABELS.constants} ${fmtNum(x.rows)}칸 갱신`;
  return `${KIND_LABELS[kind] || kind}${year ? ` ${year}` : ''} ${fmtNum(x.rows)}행(지운 ${fmtNum(x.replaced_rows)}행)`;
}

// 센터실적 상수 칸 — 파일이 빈 칸이면 «지금 (값 없음 · 그대로)», 아니면 «지금 → 파일».
export function constantCellText(cell) {
  const c = cell || {};
  if (c.file === null || c.file === undefined || c.file === '') return `${fmtNum(c.current)} (값 없음 · 그대로)`;
  return `${fmtNum(c.current)} → ${fmtNum(c.file)}`;
}

function ConstantsPreview({ it }) {
  const c = it.constants || {};
  const fields = c.fields || [];
  const rows = c.rows || [];
  const unknown = c.unknown || [];
  const duplicates = c.duplicates || [];
  return (
    <div data-testid="bpo-constants-block" style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
        {itemLabel(it)} — 현보관량 기준일: {constantsAsOfText(it)} · {replaceText(it)}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table data-testid="bpo-constants" style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>{['구분', '이름', ...fields.map((f) => `${f.label} (지금 → 파일)`)].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={2 + fields.length} style={{ ...td, color: COLORS.textMuted }}>맞출 수 있는 행이 없습니다.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={`${r.kind}:${r.name}`} data-target={`${r.kind}:${r.name}`}>
                <td data-col="kind" style={td}>{TARGET_KIND_LABELS[r.kind] || r.kind}</td>
                <td data-col="name" style={{ ...td, fontWeight: r.kind === 'center' ? 400 : 700 }}>
                  {r.name}
                  {r.label && r.label !== r.name && (
                    <span style={{ fontSize: '12px', color: COLORS.textMuted }}> (파일: {r.label} · {r.source_row}행)</span>
                  )}
                </td>
                {fields.map((f) => {
                  const cell = (r.cells || {})[f.key] || {};
                  return (
                    <td key={f.key} data-col={f.key} data-changed={cell.changed ? 'true' : 'false'}
                      style={{ ...tdNum, fontWeight: cell.changed ? 700 : 400,
                        background: cell.changed ? COLORS.primarySoft : undefined,
                        color: cell.file === null || cell.file === undefined ? COLORS.textMuted : COLORS.text }}>
                      {constantCellText(cell)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {unknown.length > 0 && (
        <div data-testid="bpo-constants-unknown" style={{ fontSize: '12px', color: COLORS.warn, marginTop: '6px' }}>
          맞출 수 없는 행(적용하지 않음): {unknown.map((u) => `${u.source_row}행 '${u.label}'`).join(', ')}
        </div>
      )}
      {duplicates.length > 0 && (
        <div data-testid="bpo-constants-duplicates" style={{ fontSize: '12px', color: COLORS.warn, marginTop: '4px' }}>
          중복 행(처음 행만 씀): {duplicates.map((d) => `${d.source_row}행 '${d.label}'(${d.first_row}행과 같은 ${d.name})`).join(', ')}
        </div>
      )}
      <div style={hint}>
        빈 칸은 기존 값을 지우지 않습니다 — 숫자가 있는 칸만 덮어씁니다. 현보관량을 덮어쓰면 그 행의 기준일 = {constantsAsOfText(it)}.
        소계·총계는 «(안양소계)»처럼 괄호로 감싼 이름이나 «총계»로 맞춥니다.
      </div>
    </div>
  );
}

function centersText(it) {
  const entries = Object.entries(it.summary?.by_center || {});
  if (!entries.length) return '—';
  const unit = it.kind === 'customers' ? '행' : '';
  return entries.map(([c, v]) => `${c || '(빈칸)'} ${fmtNum(v)}${unit}`).join(' · ');
}

function ItemRow({ it, checked, disabled, onToggle }) {
  const s = it.summary || {};
  const warnings = it.warnings || [];
  const danger = !MERGE_KINDS.includes(it.kind) && !it.rows && (it.existing_rows || 0) > 0;
  return (
    <tr data-key={it.key}>
      <td style={td}>
        <input type="checkbox" aria-label={`${it.key} 적용`} checked={checked} disabled={disabled || !!it.error}
          onChange={(e) => onToggle(it.key, e.target.checked)} />
      </td>
      <td data-col="label" style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{itemLabel(it)}</td>
      <td data-col="sheet" style={td}>{it.sheet_name}</td>
      {it.error ? (
        <td data-col="error" colSpan={5} style={{ ...td, color: COLORS.dangerText, fontWeight: 600 }}>오류 — {it.error}</td>
      ) : (
        <>
          <td data-col="rows" style={tdNum}>{fmtNum(it.rows)}</td>
          <td data-col="period" style={td}>
            {periodText(it)}
            {it.kind === 'legacy' && (s.skipped || s.blank_out_div) ? (
              <div style={{ fontSize: '12px', color: COLORS.textSub }}>
                실적 빈칸 건너뜀 {fmtNum(s.skipped || 0)}행 · 출고구분 빈칸 {fmtNum(s.blank_out_div || 0)}행(빈 값 그대로 저장)
              </div>
            ) : null}
          </td>
          <td data-col="by_center" style={{ ...td, fontSize: '12px', minWidth: '200px' }}>
            {centersText(it)}
            {(it.unknown_centers || []).length > 0 && (
              <div data-testid="bpo-unknown" style={{ color: COLORS.warn, marginTop: '2px' }}>
                실적 센터 별칭에 없음(실적 센터 없이 저장): {it.unknown_centers.map((u) => `${u.center_raw || '(빈칸)'} ${fmtNum(u.rows)}행`).join(', ')}
              </div>
            )}
          </td>
          <td data-col="warnings" style={{ ...td, fontSize: '12px' }}>
            {it.warning_count ? (
              <details>
                <summary style={{ color: COLORS.warn, cursor: 'pointer' }}>경고 {fmtNum(it.warning_count)}건</summary>
                <ul style={{ margin: '4px 0 0', paddingLeft: '16px', maxHeight: '160px', overflowY: 'auto' }}>
                  {warnings.map((w, i) => (
                    <li key={`${w.row || 'h'}-${i}`}>
                      {[w.row ? `${w.row}행` : null, w.label].filter(Boolean).join(' · ')}
                      {w.value !== null && w.value !== undefined ? ` · '${w.value}'` : ''} — {w.reason}
                    </li>
                  ))}
                </ul>
              </details>
            ) : '없음'}
          </td>
          <td data-col="replace" style={{ ...td, whiteSpace: 'nowrap', color: danger ? COLORS.dangerText : COLORS.text, fontWeight: danger ? 600 : 400 }}>
            {replaceText(it)}
          </td>
        </>
      )}
    </tr>
  );
}

export default function BpoImportSection() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);       // { file, data } — 적용은 이 file 과 지금 고른 파일이 같을 때만
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);               // { tone: 'ok' | 'err', text }
  const [imports, setImports] = useState([]);

  const loadImports = useCallback(async () => {
    try {
      const r = await centerPerfAPI.bpoImports();
      setImports(r.data?.imports || []);
    } catch (e) {
      setImports([]);
    }
  }, []);
  useEffect(() => { loadImports(); }, [loadImports]);

  const clearPreview = () => { setPreview(null); setChecked({}); };

  const runPreview = async () => {
    if (!file) return;
    clearPreview();
    setMsg(null);
    setBusy(true);
    try {
      const r = await centerPerfAPI.bpoPreview(file);
      setPreview({ file, data: r.data || { items: [] } });
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, 'BPO 파일 미리보기에 실패했습니다.') });
    } finally {
      setBusy(false);
    }
  };

  const items = preview?.data?.items || [];
  const picked = items.filter((it) => checked[it.key] && !it.error);
  const canApply = !!preview && preview.file === file && picked.length > 0 && !busy;

  const apply = async () => {
    if (!canApply) return;
    const lines = picked.map((it) => {
      if (it.kind === 'weights') {
        return `· ${itemLabel(it)}: 파일의 ${fmtNum(it.rows || 0)}일을 넣거나 바꿉니다(그 해 지금 ${fmtNum(it.existing_rows || 0)}행 · 다른 날짜 그대로)`;
      }
      if (it.kind === 'constants') return `· ${itemLabel(it)}: ${replaceText(it)} · 현보관량 기준일: ${constantsAsOfText(it)}`;
      return `· ${itemLabel(it)}: 지금 ${fmtNum(it.existing_rows || 0)}행을 지우고 ${fmtNum(it.rows || 0)}행을 넣습니다`;
    });
    const notes = [];
    if (picked.some((it) => !MERGE_KINDS.includes(it.kind))) {
      notes.push(picked.some((it) => it.kind === 'legacy')
        ? '과거 출고 RAW 는 전체를, 고객사·월 목표는 그 연도를 통째로 교체합니다.'
        : '고객사·월 목표는 그 연도를 통째로 교체합니다.');
    }
    if (picked.some((it) => it.kind === 'weights')) {
      notes.push('날짜별 가중치는 파일에 있는 날짜만 넣거나 바꿉니다(다른 날짜·수기 값은 그대로).');
    }
    if (picked.some((it) => it.kind === 'constants')) {
      notes.push('센터실적 상수는 숫자가 있는 칸만 덮어씁니다(빈 칸은 기존 값을 지우지 않습니다).');
    }
    const ok = await confirmDialog(`${notes.join(' ')}\n${lines.join('\n')}`, { danger: true });
    if (!ok) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await centerPerfAPI.bpoApply(preview.file, picked.map((it) => it.key));
      const done = (r.data?.items || []).map((x) => {
        if (x.key === 'constants') return historyItemText(x);
        const it = items.find((i) => i.key === x.key);
        return `${it ? itemLabel(it) : x.key} ${fmtNum(x.rows)}행`;
      });
      setMsg({ tone: 'ok', text: `적용했습니다 — ${done.join(' · ')}` });
      clearPreview();                                    // 지금 행 수가 바뀌었다 — 다시 보려면 다시 미리보기
      loadImports();
    } catch (e) {
      setMsg({ tone: 'err', text: detailOf(e, '적용하지 못했습니다.') });
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key, on) => setChecked((p) => ({ ...p, [key]: on }));
  const missing = preview?.data?.missing || [];

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" aria-label="BPO 파일" style={inp}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { setFile((e.target.files && e.target.files[0]) || null); clearPreview(); setMsg(null); }} />
          <button type="button" onClick={runPreview} disabled={!file || busy} style={{ ...btn(COLORS.primarySoft, COLORS.primary), opacity: !file || busy ? 0.5 : 1 }}>
            BPO 미리보기
          </button>
          <button type="button" onClick={apply} disabled={!canApply} style={{ ...btn(COLORS.primary), opacity: canApply ? 1 : 0.5 }}>
            선택 항목 적용
          </button>
        </div>
        <div style={hint}>
          시트는 이름으로 찾습니다(숨김 시트 포함) — 고객사리스트(연도) · 센터계획(연도) · '출고 RAW'로 시작하는 시트 · 요일지수(연도)(선택) ·
          센터실적(선택 — 대시보드 상수).
          미리보기는 저장하지 않습니다. 적용은 체크한 항목만 통째로 교체합니다(고객사·월 목표 = 그 연도 · 출고 RAW = 전체 ·
          날짜별 가중치 = 파일에 있는 날짜만 · 센터실적 상수 = 숫자가 있는 칸만, 빈 칸은 그대로).
        </div>
        {busy && <div style={{ ...hint, color: COLORS.textSub }}>처리 중입니다… 큰 파일은 몇 분 걸릴 수 있습니다.</div>}
        {msg && (
          <div role={msg.tone === 'err' ? 'alert' : 'status'}
            style={{ marginTop: '8px', fontSize: '13px', fontWeight: 600, color: msg.tone === 'err' ? COLORS.dangerText : COLORS.successText }}>
            {msg.text}
          </div>
        )}
        {preview && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '13px', marginBottom: '6px' }}>
              <b>{preview.data.original_name || preview.file.name}</b> · 항목 {items.length}개
            </div>
            {missing.length > 0 && (
              <div data-testid="bpo-missing" style={{ fontSize: '12px', color: COLORS.warn, marginBottom: '6px' }}>
                파일에 없는 항목: {missing.map((k) => MISSING_LABELS[k] || k).join(', ')}
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="bpo-items" style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>{['적용', '항목', '시트', '행 수', '기간', '센터별 합계', '경고', '교체'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={8} style={{ ...td, color: COLORS.textMuted }}>가져올 시트가 없습니다.</td></tr>
                  )}
                  {items.map((it) => (
                    <ItemRow key={it.key} it={it} checked={!!checked[it.key]} disabled={busy} onToggle={toggle} />
                  ))}
                </tbody>
              </table>
            </div>
            {items.filter((it) => it.kind === 'constants' && !it.error && it.constants).map((it) => (
              <ConstantsPreview key={it.key} it={it} />
            ))}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>가져오기 이력</div>
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="bpo-imports" style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>{['가져온 시각', '가져온 사람', '파일', '항목별 행 수'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {imports.length === 0 && (
                <tr><td colSpan={4} style={{ ...td, color: COLORS.textMuted }}>아직 가져온 파일이 없습니다.</td></tr>
              )}
              {imports.map((h) => (
                <tr key={h.id} data-import-id={h.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtKstDateTime(h.created_at) || '—'}</td>
                  <td style={td}>{h.importer_name}</td>
                  <td style={td}>{h.original_name}</td>
                  <td data-col="items" style={td}>
                    {(h.items || []).map(historyItemText).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
